export type PromptCategorySummary = {
  id: string;
  name: string;
};

type ApiClient = {
  get: (url: string) => any;
  post: (url: string, body: any) => any;
};

type SavePromptToLibraryParams = {
  apiClient: ApiClient;
  promptText: string;
  selectedPromptCategoryName?: string | null;
  selectedPromptName?: string | null;
  cachedCategories?: PromptCategorySummary[];
  uncategorizedName: string;
};

type SavePromptToLibraryResult = {
  categories: PromptCategorySummary[];
  categoryId: string;
  categoryName: string;
  promptName: string;
};

export const normalizePromptCategoryName = (name: string) =>
  String(name || '')
    .trim()
    .toLowerCase();

export const buildSavedPromptName = (promptText: string) =>
  String(promptText || '')
    .replace(/[\r\n]+/g, ' ')
    .trim()
    .slice(0, 45);

export const sanitizePromptCategories = (categories: any): PromptCategorySummary[] => {
  if (!Array.isArray(categories)) return [];

  const deduped = new Map<string, PromptCategorySummary>();

  categories.forEach((category) => {
    const id = String(category?.id || '').trim();
    const name = String(category?.name || '').trim();
    if (!id || !name) return;

    const key = normalizePromptCategoryName(name);
    if (!key || deduped.has(key)) return;

    deduped.set(key, { id, name });
  });

  return Array.from(deduped.values());
};

export const findPromptCategoryByName = (
  categories: PromptCategorySummary[],
  categoryName: string,
): PromptCategorySummary | undefined => {
  const normalizedCategoryName = normalizePromptCategoryName(categoryName);

  if (!normalizedCategoryName) return undefined;

  return categories.find(
    (category) => normalizePromptCategoryName(category.name) === normalizedCategoryName,
  );
};

const extractApiErrorMessage = (response: any, fallbackMessage: string) => {
  const error = response?.error;

  if (!error) return fallbackMessage;
  if (typeof error === 'string') return error;
  if (typeof error?.message === 'string' && error.message.trim()) return error.message;
  if (typeof error?.detail === 'string' && error.detail.trim()) return error.detail;

  return fallbackMessage;
};

const ensureNoApiError = (response: any, fallbackMessage: string) => {
  if (response?.error) {
    throw new Error(extractApiErrorMessage(response, fallbackMessage));
  }

  return response;
};

const createPromptCategory = async (
  apiClient: ApiClient,
  categoryName: string,
): Promise<PromptCategorySummary> => {
  const response = ensureNoApiError(
    await apiClient.post('/prompt-categories', { name: categoryName }),
    'Failed to create category.',
  );

  const id = String(response?.id || '').trim();
  const name = String(response?.name || categoryName).trim();

  if (!id || !name) {
    throw new Error('Failed to create category.');
  }

  return { id, name };
};

const fetchPromptCategories = async (apiClient: ApiClient): Promise<PromptCategorySummary[]> => {
  const response = ensureNoApiError(
    await apiClient.get('/prompt-categories'),
    'Failed to load categories.',
  );

  return sanitizePromptCategories(response);
};

export async function savePromptToLibrary({
  apiClient,
  promptText,
  selectedPromptCategoryName,
  selectedPromptName,
  cachedCategories = [],
  uncategorizedName,
}: SavePromptToLibraryParams): Promise<SavePromptToLibraryResult> {
  const promptName = String(selectedPromptName || '').trim() || buildSavedPromptName(promptText);

  if (!String(promptText || '').trim() || !promptName) {
    throw new Error('Prompt cannot be empty.');
  }

  let categories = sanitizePromptCategories(cachedCategories);
  let categoryName = String(selectedPromptCategoryName || '').trim();
  let category = categoryName ? findPromptCategoryByName(categories, categoryName) : undefined;

  if (!categoryName) {
    categories = await fetchPromptCategories(apiClient);
    categoryName = String(uncategorizedName || '').trim();
    category = findPromptCategoryByName(categories, categoryName);
  }

  if (!category) {
    category = await createPromptCategory(apiClient, categoryName);
    categories = sanitizePromptCategories([...categories, category]);
  }

  ensureNoApiError(
    await apiClient.post(`/prompt-categories/${category.id}/prompts`, {
      name: promptName,
      prompt: promptText,
    }),
    'Failed to create prompt.',
  );

  return {
    categories,
    categoryId: category.id,
    categoryName: category.name,
    promptName,
  };
}
